import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SchemaRoundedIcon from '@mui/icons-material/SchemaRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import CapabilityExecutionProfiles from '../components/CapabilityExecutionProfiles';
import CapabilityLocationFields from '../components/CapabilityLocationFields';
import Header from '../components/Header';
import InventoryImage from '../components/InventoryImage';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import { canManageBusinessUnit } from '../utils/businessUnitAccess';
import { capabilityInputsForProfiles } from '../utils/capabilityExecutionProfiles';
import {
  ACCEPTANCE_MODES,
  ADDITIONAL_INPUT_TYPES,
  CAPABILITY_STATUSES,
  CAPABILITY_TYPES,
  CAPABILITY_VISIBILITIES,
  FULFILLMENT_MODES,
  OUTPUT_KINDS,
  REQUEST_FIELD_TIMING_OPTIONS,
  additionalInputsForPayload,
  additionalRequestSchemaFromInputs,
  blankAdditionalInput,
  blankMatchingCriterion,
  blankOutput,
  eligibleMatchingFields,
  fieldsFromAdditionalRequestSchema,
  fieldsFromSharedSchema,
  formatCapabilityLabel,
  matchingProfileFromCriteria,
  normalizeObject,
  outputsForPayload,
  slugifyCapabilityTitle,
  uniqueSchemaKey,
} from '../utils/capabilityContract';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const steps = ['Offering', 'Request & Matching', 'Inputs & Outputs', 'Assets & Fulfillment'];

function emptyForm(businessUnitId = '') {
  return {
    business_unit_id: businessUnitId,
    name: '',
    description: '',
    capability_type: 'hybrid',
    capability_image_id: '',
    location_address: '',
    latitude: '',
    longitude: '',
    need_category_id: '',
    matching_criteria: [],
    additional_inputs: [],
    outputs: [],
    asset_ids: [],
    execution_profiles: {},
    status: 'draft',
    visibility: 'partner',
    list_on_capabilities_explorer: false,
    allow_direct_requests: true,
    acceptance_mode: 'manual',
    fulfillment_mode: 'manual',
  };
}

function optionLabel(options, value) {
  return options.find((option) => option.value === value)?.label || formatCapabilityLabel(value);
}

function coordinateForPayload(value) {
  const text = String(value ?? '').trim();
  return text ? Number(text) : null;
}

export function ContractFieldList({
  title,
  fields,
  emptyLabel,
  locked = false,
  collapsedByDefault = false,
}) {
  const [expanded, setExpanded] = useState(!collapsedByDefault);
  const fieldCountLabel = `${fields.length} ${fields.length === 1 ? 'field' : 'fields'}`;

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {locked ? <LockRoundedIcon fontSize="small" color="action" /> : <SchemaRoundedIcon fontSize="small" color="primary" />}
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {title}
            </Typography>
            <Chip size="small" label={fieldCountLabel} variant="outlined" />
          </Stack>
          {collapsedByDefault && fields.length ? (
            <Button size="small" variant="text" onClick={() => setExpanded((value) => !value)}>
              {expanded ? 'Hide Shared Fields' : 'View All Shared Fields'}
            </Button>
          ) : null}
        </Stack>
      </Box>
      {!expanded && fields.length ? (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {fieldCountLabel} inherited from the selected Need Category.
          </Typography>
        </Box>
      ) : null}
      {expanded ? <Stack divider={<Divider />} sx={{ px: 2 }}>
        {fields.length ? fields.map((field) => (
          <Stack
            key={field.key}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="space-between"
            sx={{ py: 1.25 }}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {locked ? <LockRoundedIcon fontSize="inherit" color="action" /> : null}
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {field.display_name || formatCapabilityLabel(field.key)}
                </Typography>
              </Stack>
              {field.description ? (
                <Typography variant="caption" color="text.secondary">
                  {field.description}
                </Typography>
              ) : null}
            </Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={formatCapabilityLabel(field.type)} variant="outlined" />
              {field.required ? <Chip size="small" color="primary" label="Required" /> : <Chip size="small" label="Optional" variant="outlined" />}
            </Stack>
          </Stack>
        )) : (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {emptyLabel}
            </Typography>
          </Box>
        )}
      </Stack> : null}
    </Paper>
  );
}

function normalizeSelectOptions(options) {
  return (Array.isArray(options) ? options : [])
    .map((option) => {
      if (option && typeof option === 'object') {
        const value = String(option.value ?? option.key ?? option.label ?? '').trim();
        return value ? { value, label: String(option.label ?? value) } : null;
      }
      const value = String(option ?? '').trim();
      return value ? { value, label: value } : null;
    })
    .filter(Boolean);
}

function MatchingCriterionConfig({ criterion, field, onChange }) {
  const config = criterion.config || {};
  const options = normalizeSelectOptions(field.options);

  if (field.match_mode === 'numeric_range') {
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          label="Minimum"
          type="number"
          size="small"
          value={config.min ?? ''}
          onChange={(event) => onChange({ config: { ...config, min: event.target.value } })}
          fullWidth
        />
        <TextField
          label="Maximum"
          type="number"
          size="small"
          value={config.max ?? ''}
          onChange={(event) => onChange({ config: { ...config, max: event.target.value } })}
          fullWidth
        />
      </Stack>
    );
  }

  if (field.match_mode === 'allowed_values') {
    const selected = Array.isArray(config.allowed_values) ? config.allowed_values : [];
    return (
      <FormControl fullWidth size="small">
        <InputLabel id={`${criterion.id}-allowed-values-label`}>Allowed Values</InputLabel>
        <Select
          labelId={`${criterion.id}-allowed-values-label`}
          multiple
          label="Allowed Values"
          value={selected}
          input={<OutlinedInput label="Allowed Values" />}
          onChange={(event) => onChange({ config: { ...config, allowed_values: event.target.value } })}
          renderValue={(values) => values.map((value) => options.find((option) => option.value === value)?.label || value).join(', ')}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={selected.includes(option.value)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (field.match_mode === 'boolean_support') {
    return (
      <TextField
        select
        label="Support"
        size="small"
        value={config.support || 'either'}
        onChange={(event) => onChange({ config: { ...config, support: event.target.value } })}
        fullWidth
      >
        <MenuItem value="yes">Yes</MenuItem>
        <MenuItem value="no">No</MenuItem>
        <MenuItem value="either">Either</MenuItem>
      </TextField>
    );
  }

  if (options.length) {
    return (
      <TextField
        select
        label="Value"
        size="small"
        value={config.value ?? ''}
        onChange={(event) => onChange({ config: { ...config, value: event.target.value } })}
        fullWidth
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </TextField>
    );
  }

  return (
    <TextField
      label="Value"
      size="small"
      value={config.value ?? ''}
      onChange={(event) => onChange({ config: { ...config, value: event.target.value } })}
      fullWidth
    />
  );
}

export function MatchingProfileEditor({ criteria, sharedFields, onChange }) {
  const matchableFields = eligibleMatchingFields(sharedFields);
  const selectedKeys = new Set((criteria || []).map((criterion) => criterion.field_key).filter(Boolean));
  const updateCriterion = (id, patch) => {
    onChange(criteria.map((criterion) => (criterion.id === id ? { ...criterion, ...patch } : criterion)));
  };
  const addCriterion = () => {
    const criterion = blankMatchingCriterion(sharedFields, criteria);
    if (criterion) {
      onChange([...criteria, criterion]);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Matching Profile
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddCircleRoundedIcon />}
            onClick={addCriterion}
            disabled={matchableFields.length === 0 || selectedKeys.size >= matchableFields.length}
          >
            Add Matching Criterion
          </Button>
        </Stack>
        {matchableFields.length === 0 ? (
          <Alert severity="info">No deterministic matching fields are defined for this Need Category.</Alert>
        ) : null}
        {criteria.length ? criteria.map((criterion) => {
          const field = matchableFields.find((item) => item.key === criterion.field_key) || matchableFields[0];
          const availableFields = matchableFields.filter((candidate) => (
            candidate.key === criterion.field_key || !selectedKeys.has(candidate.key)
          ));
          return (
            <Box key={criterion.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Stack spacing={1.25}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <TextField
                    select
                    label="Need Category Field"
                    size="small"
                    value={criterion.field_key}
                    onChange={(event) => {
                      const nextField = matchableFields.find((item) => item.key === event.target.value);
                      updateCriterion(criterion.id, {
                        field_key: event.target.value,
                        match_mode: nextField?.match_mode || 'exact_value',
                        config: {},
                      });
                    }}
                    fullWidth
                  >
                    {availableFields.map((candidate) => (
                      <MenuItem key={candidate.key} value={candidate.key}>
                        {candidate.display_name || formatCapabilityLabel(candidate.key)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Chip
                    label={formatCapabilityLabel(field.match_mode)}
                    variant="outlined"
                    sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                  />
                  <Tooltip title="Remove criterion">
                    <IconButton color="error" onClick={() => onChange(criteria.filter((item) => item.id !== criterion.id))}>
                      <DeleteRoundedIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <MatchingCriterionConfig
                  criterion={criterion}
                  field={field}
                  onChange={(patch) => updateCriterion(criterion.id, patch)}
                />
              </Stack>
            </Box>
          );
        }) : (
          <Typography variant="body2" color="text.secondary">
            No matching criteria set.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function listValues(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => (item && typeof item === 'object' ? item.value ?? item.key ?? item.label : item))
      .map((item) => String(item ?? '').trim())
      .filter(Boolean);
  }
  return String(value || '')
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseJsonObject(text, label) {
  if (!String(text || '').trim()) {
    return {};
  }
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (_error) {
    throw new Error(`${label} must be valid JSON.`);
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`${label} must be a JSON object.`);
  }
  return parsed;
}

function jsonDraftText(value) {
  const normalized = normalizeObject(value);
  return Object.keys(normalized).length ? JSON.stringify(normalized, null, 2) : '';
}

function withDisplayOrder(items) {
  return items.map((item, index) => ({ ...item, display_order: index }));
}

function moveListItem(items, index, direction) {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= items.length) {
    return items;
  }
  const nextItems = [...items];
  const [item] = nextItems.splice(index, 1);
  nextItems.splice(nextIndex, 0, item);
  return withDisplayOrder(nextItems);
}

function timingLabel(value) {
  return REQUEST_FIELD_TIMING_OPTIONS.find((option) => option.value === value)?.label || 'Optional';
}

function fieldTypeLabel(value) {
  return ADDITIONAL_INPUT_TYPES.find((option) => option.value === value)?.label || formatCapabilityLabel(value);
}

function outputTypeLabel(value) {
  return OUTPUT_KINDS.find((option) => option.value === value)?.label || formatCapabilityLabel(value);
}

function validateNumericBounds(min, max, label) {
  const minText = String(min ?? '').trim();
  const maxText = String(max ?? '').trim();
  if (minText && Number.isNaN(Number(minText))) {
    throw new Error(`${label} minimum must be a number.`);
  }
  if (maxText && Number.isNaN(Number(maxText))) {
    throw new Error(`${label} maximum must be a number.`);
  }
  if (minText && maxText && Number(minText) > Number(maxText)) {
    throw new Error(`${label} minimum cannot exceed maximum.`);
  }
}

function inputDraftFromInput(input) {
  return {
    ...blankAdditionalInput(),
    ...(input || {}),
    options: Array.isArray(input?.options) ? listValues(input.options).join('\n') : input?.options || '',
    accepted_file_types: Array.isArray(input?.accepted_content_types)
      ? listValues(input.accepted_content_types).join(', ')
      : input?.accepted_file_types || '',
    data_schema_text: jsonDraftText(input?.data_schema),
  };
}

function outputDraftFromOutput(output) {
  return {
    ...blankOutput(),
    ...(output || {}),
    data_schema_text: jsonDraftText(output?.data_schema),
  };
}

function normalizeInputDraft(draft, inputs, editingIndex) {
  const displayName = String(draft.display_name || '').trim();
  if (!displayName) {
    throw new Error('Field Name is required.');
  }

  const existingKeys = inputs
    .filter((_item, index) => index !== editingIndex)
    .map((input) => input.key)
    .filter(Boolean);
  const existingInput = editingIndex >= 0 ? inputs[editingIndex] : null;
  const type = draft.type || 'text';
  const normalized = {
    ...blankAdditionalInput(),
    ...draft,
    key: existingInput?.key || draft.key || uniqueSchemaKey(displayName, existingKeys, 'field'),
    display_name: displayName,
    description: String(draft.description || '').trim(),
    type,
    requirement_stage: draft.requirement_stage || 'request',
    display_order: editingIndex >= 0 ? editingIndex : inputs.length,
  };
  delete normalized.data_schema_text;

  if (type === 'selection') {
    const options = listValues(draft.options);
    if (!options.length) {
      throw new Error('Selection fields need at least one option.');
    }
    normalized.options = options.join('\n');
  }

  if (type === 'number') {
    validateNumericBounds(draft.min, draft.max, displayName);
    normalized.unit = String(draft.unit || '').trim();
    normalized.min = String(draft.min ?? '').trim();
    normalized.max = String(draft.max ?? '').trim();
  }

  if (type === 'file') {
    normalized.accepted_file_types = listValues(draft.accepted_file_types).join(', ');
    normalized.multiple = Boolean(draft.multiple);
  }

  if (type === 'structured_data') {
    normalized.data_schema = parseJsonObject(draft.data_schema_text, 'Data Schema JSON');
  }

  if (type === 'physical_item') {
    normalized.physical_item_details = String(draft.physical_item_details || '').trim();
  }

  if (type === 'reference') {
    normalized.reference_target = String(draft.reference_target || '').trim();
  }

  return normalized;
}

function normalizeOutputDraft(draft, outputs, editingIndex) {
  const name = String(draft.name || '').trim();
  if (!name) {
    throw new Error('Output Name is required.');
  }

  const existingKeys = outputs
    .filter((_item, index) => index !== editingIndex)
    .map((output) => output.key)
    .filter(Boolean);
  const existingOutput = editingIndex >= 0 ? outputs[editingIndex] : null;
  const kind = draft.kind || 'file';
  const normalized = {
    ...blankOutput(),
    ...draft,
    key: existingOutput?.key || draft.key || uniqueSchemaKey(name, existingKeys, 'output'),
    name,
    description: String(draft.description || '').trim(),
    kind,
    required: Boolean(draft.required),
    semantic_type: draft.semantic_type || '',
    data_schema: normalizeObject(draft.data_schema),
    display_order: editingIndex >= 0 ? editingIndex : outputs.length,
  };
  delete normalized.data_schema_text;

  if (['structured_data', 'value'].includes(kind)) {
    normalized.data_schema = parseJsonObject(draft.data_schema_text, 'Data Schema JSON');
  }

  return normalized;
}

function inputConfigSummary(input) {
  if (input.type === 'selection') {
    const options = listValues(input.options);
    return options.length ? `${options.length} options` : 'Selection';
  }
  if (input.type === 'number') {
    const parts = [];
    if (input.unit) {
      parts.push(input.unit);
    }
    if (input.min !== '' && input.min !== null && typeof input.min !== 'undefined') {
      parts.push(`Min ${input.min}`);
    }
    if (input.max !== '' && input.max !== null && typeof input.max !== 'undefined') {
      parts.push(`Max ${input.max}`);
    }
    return parts.join(' / ') || 'Number';
  }
  if (input.type === 'file') {
    const accepted = listValues(input.accepted_file_types);
    const parts = [];
    if (accepted.length) {
      parts.push(accepted.join(', '));
    }
    if (input.multiple) {
      parts.push('Multiple files');
    }
    return parts.join(' / ') || 'File upload';
  }
  if (input.type === 'structured_data') {
    return Object.keys(normalizeObject(input.data_schema)).length ? 'Schema configured' : 'Structured data';
  }
  if (input.type === 'physical_item') {
    return input.physical_item_details || 'Physical item';
  }
  if (input.type === 'reference') {
    return input.reference_target ? `Reference: ${input.reference_target}` : 'Reference';
  }
  return fieldTypeLabel(input.type);
}

function outputConfigSummary(output) {
  if (['structured_data', 'value'].includes(output.kind)) {
    return Object.keys(normalizeObject(output.data_schema)).length ? 'Schema configured' : outputTypeLabel(output.kind);
  }
  return outputTypeLabel(output.kind);
}

function AdditionalInputDialog({ open, draft, isEditing, error, onChange, onClose, onSave }) {
  if (!draft) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditing ? 'Edit Request Field' : 'Add Request Field'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label="Field Name"
            value={draft.display_name}
            onChange={(event) => onChange({ display_name: event.target.value })}
            required
            fullWidth
          />
          <TextField
            label="Description"
            value={draft.description}
            onChange={(event) => onChange({ description: event.target.value })}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField
            select
            label="Input Type"
            value={draft.type}
            onChange={(event) => onChange({ type: event.target.value })}
            fullWidth
          >
            {ADDITIONAL_INPUT_TYPES.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="When is this information needed?"
            value={draft.requirement_stage}
            onChange={(event) => onChange({ requirement_stage: event.target.value })}
            fullWidth
          >
            {REQUEST_FIELD_TIMING_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          {draft.type === 'selection' ? (
            <TextField
              label="Options"
              value={draft.options}
              onChange={(event) => onChange({ options: event.target.value })}
              fullWidth
              multiline
              minRows={3}
            />
          ) : null}
          {draft.type === 'number' ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                label="Unit"
                value={draft.unit}
                onChange={(event) => onChange({ unit: event.target.value })}
                fullWidth
              />
              <TextField
                label="Minimum"
                type="number"
                value={draft.min}
                onChange={(event) => onChange({ min: event.target.value })}
                fullWidth
              />
              <TextField
                label="Maximum"
                type="number"
                value={draft.max}
                onChange={(event) => onChange({ max: event.target.value })}
                fullWidth
              />
            </Stack>
          ) : null}
          {draft.type === 'file' ? (
            <Stack spacing={1}>
              <TextField
                label="Accepted File Types"
                value={draft.accepted_file_types}
                onChange={(event) => onChange({ accepted_file_types: event.target.value })}
                fullWidth
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(draft.multiple)}
                    onChange={(event) => onChange({ multiple: event.target.checked })}
                  />
                )}
                label="Allow Multiple Files"
              />
            </Stack>
          ) : null}
          {draft.type === 'structured_data' ? (
            <TextField
              label="Data Schema JSON"
              value={draft.data_schema_text}
              onChange={(event) => onChange({ data_schema_text: event.target.value })}
              fullWidth
              multiline
              minRows={5}
            />
          ) : null}
          {draft.type === 'physical_item' ? (
            <TextField
              label="Physical Item Details"
              value={draft.physical_item_details}
              onChange={(event) => onChange({ physical_item_details: event.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
          ) : null}
          {draft.type === 'reference' ? (
            <TextField
              label="Reference Target"
              value={draft.reference_target}
              onChange={(event) => onChange({ reference_target: event.target.value })}
              fullWidth
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function OutputDialog({ open, draft, isEditing, error, onChange, onClose, onSave }) {
  if (!draft) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditing ? 'Edit Output' : 'Add Output'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label="Output Name"
            value={draft.name}
            onChange={(event) => onChange({ name: event.target.value })}
            required
            fullWidth
          />
          <TextField
            label="Description"
            value={draft.description}
            onChange={(event) => onChange({ description: event.target.value })}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField
            select
            label="Output Type"
            value={draft.kind}
            onChange={(event) => onChange({ kind: event.target.value })}
            fullWidth
          >
            {OUTPUT_KINDS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(draft.required)}
                onChange={(event) => onChange({ required: event.target.checked })}
              />
            )}
            label="Required Output"
          />
          {['structured_data', 'value'].includes(draft.kind) ? (
            <TextField
              label="Data Schema JSON"
              value={draft.data_schema_text}
              onChange={(event) => onChange({ data_schema_text: event.target.value })}
              fullWidth
              multiline
              minRows={5}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export function AdditionalInputsEditor({ inputs, onChange, onPreview }) {
  const [dialogState, setDialogState] = useState(null);
  const [dialogError, setDialogError] = useState('');

  const openNewDialog = () => {
    setDialogError('');
    setDialogState({ index: -1, draft: inputDraftFromInput(null) });
  };
  const openEditDialog = (input, index) => {
    setDialogError('');
    setDialogState({ index, draft: inputDraftFromInput(input) });
  };
  const closeDialog = () => {
    setDialogState(null);
    setDialogError('');
  };
  const updateDraft = (patch) => {
    setDialogState((current) => current ? ({ ...current, draft: { ...current.draft, ...patch } }) : current);
  };
  const saveDraft = () => {
    try {
      const normalized = normalizeInputDraft(dialogState.draft, inputs, dialogState.index);
      const nextInputs = dialogState.index >= 0
        ? inputs.map((input, index) => (index === dialogState.index ? normalized : input))
        : [...inputs, normalized];
      onChange(withDisplayOrder(nextInputs));
      closeDialog();
    } catch (err) {
      setDialogError(err?.message || 'Unable to save request field.');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Provider-Specific Request Fields
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
            {onPreview ? (
              <Button size="small" variant="outlined" startIcon={<VisibilityRoundedIcon />} onClick={onPreview}>
                Preview Request Form
              </Button>
            ) : null}
            <Button size="small" variant="contained" startIcon={<AddCircleRoundedIcon />} onClick={openNewDialog}>
              Add Request Field
            </Button>
          </Stack>
        </Stack>
        {inputs.length ? inputs.map((input, index) => (
          <Box key={input.id || input.key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.25}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                  {input.display_name || formatCapabilityLabel(input.key)}
                </Typography>
                {input.description ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, whiteSpace: 'pre-wrap' }}>
                    {input.description}
                  </Typography>
                ) : null}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  <Chip size="small" label={fieldTypeLabel(input.type)} variant="outlined" />
                  <Chip
                    size="small"
                    color={input.requirement_stage === 'optional' ? 'default' : 'primary'}
                    variant={input.requirement_stage === 'optional' ? 'outlined' : 'filled'}
                    label={timingLabel(input.requirement_stage)}
                  />
                  <Chip size="small" label={inputConfigSummary(input)} variant="outlined" />
                </Stack>
              </Box>
              <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Tooltip title="Move up">
                  <span>
                    <IconButton size="small" disabled={index === 0} onClick={() => onChange(moveListItem(inputs, index, -1))}>
                      <ArrowUpwardRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton size="small" disabled={index === inputs.length - 1} onClick={() => onChange(moveListItem(inputs, index, 1))}>
                      <ArrowDownwardRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Edit request field">
                  <IconButton size="small" onClick={() => openEditDialog(input, index)}>
                    <EditRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove request field">
                  <IconButton size="small" color="error" onClick={() => onChange(withDisplayOrder(inputs.filter((_item, itemIndex) => itemIndex !== index)))}>
                    <DeleteRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        )) : (
          <Typography variant="body2" color="text.secondary">
            No provider-specific request fields defined.
          </Typography>
        )}
      </Stack>
      <AdditionalInputDialog
        open={Boolean(dialogState)}
        draft={dialogState?.draft}
        isEditing={dialogState?.index >= 0}
        error={dialogError}
        onChange={updateDraft}
        onClose={closeDialog}
        onSave={saveDraft}
      />
    </Paper>
  );
}

export function OutputsEditor({ outputs, onChange }) {
  const [dialogState, setDialogState] = useState(null);
  const [dialogError, setDialogError] = useState('');

  const openNewDialog = () => {
    setDialogError('');
    setDialogState({ index: -1, draft: outputDraftFromOutput(null) });
  };
  const openEditDialog = (output, index) => {
    setDialogError('');
    setDialogState({ index, draft: outputDraftFromOutput(output) });
  };
  const closeDialog = () => {
    setDialogState(null);
    setDialogError('');
  };
  const updateDraft = (patch) => {
    setDialogState((current) => current ? ({ ...current, draft: { ...current.draft, ...patch } }) : current);
  };
  const saveDraft = () => {
    try {
      const normalized = normalizeOutputDraft(dialogState.draft, outputs, dialogState.index);
      const nextOutputs = dialogState.index >= 0
        ? outputs.map((output, index) => (index === dialogState.index ? normalized : output))
        : [...outputs, normalized];
      onChange(withDisplayOrder(nextOutputs));
      closeDialog();
    } catch (err) {
      setDialogError(err?.message || 'Unable to save output.');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Expected Outputs
          </Typography>
          <Button size="small" variant="contained" startIcon={<AddCircleRoundedIcon />} onClick={openNewDialog}>
            Add Output
          </Button>
        </Stack>
        {outputs.length ? outputs.map((output, index) => (
          <Box key={output.id || output.output_id || output.key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.25}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                  {output.name || formatCapabilityLabel(output.key)}
                </Typography>
                {output.description ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, whiteSpace: 'pre-wrap' }}>
                    {output.description}
                  </Typography>
                ) : null}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  <Chip size="small" label={outputTypeLabel(output.kind)} variant="outlined" />
                  <Chip
                    size="small"
                    color={output.required ? 'primary' : 'default'}
                    variant={output.required ? 'filled' : 'outlined'}
                    label={output.required ? 'Required Output' : 'Optional Output'}
                  />
                  <Chip size="small" label={outputConfigSummary(output)} variant="outlined" />
                </Stack>
              </Box>
              <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Tooltip title="Move up">
                  <span>
                    <IconButton size="small" disabled={index === 0} onClick={() => onChange(moveListItem(outputs, index, -1))}>
                      <ArrowUpwardRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton size="small" disabled={index === outputs.length - 1} onClick={() => onChange(moveListItem(outputs, index, 1))}>
                      <ArrowDownwardRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Edit output">
                  <IconButton size="small" onClick={() => openEditDialog(output, index)}>
                    <EditRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove output">
                  <IconButton size="small" color="error" onClick={() => onChange(withDisplayOrder(outputs.filter((_item, itemIndex) => itemIndex !== index)))}>
                    <DeleteRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        )) : (
          <Typography variant="body2" color="text.secondary">
            No outputs defined.
          </Typography>
        )}
      </Stack>
      <OutputDialog
        open={Boolean(dialogState)}
        draft={dialogState?.draft}
        isEditing={dialogState?.index >= 0}
        error={dialogError}
        onChange={updateDraft}
        onClose={closeDialog}
        onSave={saveDraft}
      />
    </Paper>
  );
}

function selectOptionsForField(field) {
  if (Array.isArray(field?.options)) {
    return normalizeSelectOptions(field.options);
  }
  return listValues(field?.options).map((value) => ({ value, label: value }));
}

function PreviewFieldControl({ field, locked = false }) {
  const key = field.key || field.name || field.display_name;
  const label = field.display_name || field.label || formatCapabilityLabel(key);
  const type = String(field.type || 'text').toLowerCase();
  const description = field.description || '';
  const required = Boolean(field.required);
  const commonProps = {
    label,
    value: '',
    fullWidth: true,
    size: 'small',
    required,
    disabled: true,
    helperText: description || ' ',
  };

  if (['select', 'selection'].includes(type)) {
    const options = selectOptionsForField(field);
    return (
      <TextField {...commonProps} select>
        <MenuItem value=""> </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </TextField>
    );
  }

  if (['multiselect', 'tags'].includes(type)) {
    const options = selectOptionsForField(field);
    return (
      <FormControl fullWidth size="small" required={required} disabled>
        <InputLabel id={`preview-${key}-label`}>{label}</InputLabel>
        <Select
          labelId={`preview-${key}-label`}
          multiple
          value={[]}
          input={<OutlinedInput label={label} />}
          renderValue={(selected) => selected.join(', ')}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={false} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
        {description ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
            {description}
          </Typography>
        ) : null}
      </FormControl>
    );
  }

  if (type === 'boolean') {
    return (
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          px: 1.5,
          py: 1,
          bgcolor: locked ? 'background.default' : 'background.paper',
        }}
      >
        <FormControlLabel control={<Switch checked={false} disabled />} label={label} />
        {description ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {description}
          </Typography>
        ) : null}
      </Box>
    );
  }

  if (type === 'file') {
    return (
      <Box>
        <Button variant="outlined" disabled startIcon={<CloudUploadRoundedIcon />} fullWidth sx={{ justifyContent: 'flex-start' }}>
          {label}
        </Button>
        {description ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 1.75 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
    );
  }

  if (type === 'textarea') {
    return <TextField {...commonProps} multiline minRows={3} />;
  }

  if (['structured_data', 'physical_item', 'reference'].includes(type)) {
    return <TextField {...commonProps} multiline minRows={3} />;
  }

  if (type === 'number' || type === 'range') {
    return <TextField {...commonProps} type="number" inputProps={{ min: field.min, max: field.max }} />;
  }

  if (type === 'date') {
    return <TextField {...commonProps} type="date" InputLabelProps={{ shrink: true }} />;
  }

  if (type === 'datetime') {
    return <TextField {...commonProps} type="datetime-local" InputLabelProps={{ shrink: true }} />;
  }

  return <TextField {...commonProps} />;
}

function PreviewFieldSection({ title, fields, locked = false }) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: locked ? 'divider' : 'primary.light',
        borderRadius: 1,
        p: 2,
        bgcolor: locked ? 'background.default' : 'background.paper',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          {locked ? <LockRoundedIcon fontSize="small" color="action" /> : <SchemaRoundedIcon fontSize="small" color="primary" />}
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
          <Chip size="small" label={`${fields.length}`} variant="outlined" />
        </Stack>
        {fields.length ? fields.map((field) => (
          <PreviewFieldControl key={field.key || field.name} field={field} locked={locked} />
        )) : (
          <Typography variant="body2" color="text.secondary">
            No fields defined.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export function RequestFormPreviewDialog({ open, onClose, sharedFields, providerFields }) {
  const shared = Array.isArray(sharedFields) ? sharedFields : [];
  const provider = Array.isArray(providerFields) ? providerFields : [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Preview Request Form</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <PreviewFieldSection title="Shared Need Category Fields" fields={shared} locked />
          <PreviewFieldSection title="Provider-Specific Fields" fields={provider} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export function AssetPicker({ assets, selectedAssetIds, loading, onChange }) {
  const selected = new Set(selectedAssetIds);
  const toggleAsset = (assetId) => {
    onChange(
      selected.has(assetId)
        ? selectedAssetIds.filter((value) => value !== assetId)
        : [...selectedAssetIds, assetId],
    );
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Inventory2RoundedIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Supporting Assets
          </Typography>
          <Chip size="small" label={`${selectedAssetIds.length} selected`} variant="outlined" />
        </Stack>
      </Box>
      {loading ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading assets...</Typography>
        </Stack>
      ) : assets.length ? (
        <Stack divider={<Divider />}>
          {assets.map((asset) => (
            <Stack
              key={asset.asset_id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ px: 2, py: 1.25 }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Checkbox
                  checked={selected.has(asset.asset_id)}
                  onChange={() => toggleAsset(asset.asset_id)}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {asset.asset_name || 'Unnamed Asset'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {asset.asset_type_display_name || formatCapabilityLabel(asset.asset_type)}
                  </Typography>
                </Box>
              </Stack>
              <Chip size="small" label={asset.location_label || asset.business_unit_display_name || 'Asset'} variant="outlined" />
            </Stack>
          ))}
        </Stack>
      ) : (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No assets registered for this Business Unit.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default function CapabilityCreatePage(props) {
  const { businessUnitId: pathBusinessUnitId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const partnerProfile = useSelector((state) => state.auth.partnerProfile);
  const scopedBusinessUnitId = pathBusinessUnitId || '';
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(() => emptyForm(scopedBusinessUnitId));
  const [businessUnits, setBusinessUnits] = useState([]);
  const [needCategories, setNeedCategories] = useState([]);
  const [needCategorySchema, setNeedCategorySchema] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState('');

  const updateForm = (patch) => setForm((current) => ({ ...current, ...patch }));

  const manageableBusinessUnits = useMemo(() => {
    return businessUnits.filter((unit) => canManageBusinessUnit(
      partnerProfile,
      unit.business_unit_id,
      unit.parent_partner_id,
    ));
  }, [businessUnits, partnerProfile]);

  const selectedBusinessUnit = useMemo(() => {
    return businessUnits.find((unit) => unit.business_unit_id === form.business_unit_id) || null;
  }, [businessUnits, form.business_unit_id]);

  const selectedCategory = useMemo(() => {
    return needCategories.find((category) => category.category_id === form.need_category_id) || null;
  }, [form.need_category_id, needCategories]);

  const sharedFields = useMemo(() => fieldsFromSharedSchema(needCategorySchema), [needCategorySchema]);
  const capabilityAdditionalFields = useMemo(() => {
    try {
      return fieldsFromAdditionalRequestSchema(additionalRequestSchemaFromInputs(form.additional_inputs));
    } catch (_error) {
      return [];
    }
  }, [form.additional_inputs]);
  const executionProfileInputs = useMemo(
    () => capabilityInputsForProfiles(sharedFields, capabilityAdditionalFields),
    [sharedFields, capabilityAdditionalFields],
  );
  const selectedAssets = useMemo(() => {
    const selectedIds = new Set(form.asset_ids);
    return assets.filter((asset) => selectedIds.has(asset.asset_id));
  }, [assets, form.asset_ids]);

  useEffect(() => {
    let isActive = true;
    async function loadInitialData() {
      setLoading(true);
      setError('');
      try {
        const [businessUnitResponse, needCategoryResponse] = await Promise.all([
          axiosInstance.get('/partners/user/business-units'),
          axiosInstance.get('/requests/need-categories'),
        ]);
        if (!isActive) {
          return;
        }
        const nextBusinessUnits = Array.isArray(businessUnitResponse.data?.business_units)
          ? businessUnitResponse.data.business_units
          : [];
        setBusinessUnits(nextBusinessUnits);
        setNeedCategories(Array.isArray(needCategoryResponse.data) ? needCategoryResponse.data : []);
      } catch (err) {
        if (isActive) {
          setError(err?.response?.data?.message || err?.message || 'Unable to load capability setup data.');
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    }
    loadInitialData();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (scopedBusinessUnitId && form.business_unit_id !== scopedBusinessUnitId) {
      updateForm({ business_unit_id: scopedBusinessUnitId });
    }
  }, [form.business_unit_id, scopedBusinessUnitId]);

  useEffect(() => {
    let isActive = true;
    async function loadSchema() {
      setNeedCategorySchema(null);
      if (!selectedCategory) {
        return;
      }
      if (!selectedCategory.default_request_schema_id || !selectedCategory.default_request_schema_version) {
        return;
      }
      setSchemaLoading(true);
      setError('');
      try {
        const response = await axiosInstance.post('/requests/schema', {
          schema_id: selectedCategory.default_request_schema_id,
          schema_version: selectedCategory.default_request_schema_version,
          version: selectedCategory.default_request_schema_version,
        });
        if (isActive) {
          setNeedCategorySchema(response.data?.schema || response.data || null);
        }
      } catch (err) {
        if (isActive) {
          setError(err?.response?.data?.message || err?.message || 'Unable to load the Need Category request schema.');
        }
      } finally {
        if (isActive) {
          setSchemaLoading(false);
        }
      }
    }
    loadSchema();
    return () => {
      isActive = false;
    };
  }, [selectedCategory]);

  useEffect(() => {
    let isActive = true;
    async function loadAssets() {
      setAssets([]);
      if (!form.business_unit_id) {
        return;
      }
      setAssetsLoading(true);
      try {
        const response = await axiosInstance.get('/partners/user/assets', {
          params: { business_unit_id: form.business_unit_id },
        });
        if (isActive) {
          setAssets(Array.isArray(response.data?.assets) ? response.data.assets : []);
        }
      } catch (err) {
        if (isActive) {
          setError(err?.response?.data?.message || err?.message || 'Unable to load Business Unit assets.');
        }
      } finally {
        if (isActive) {
          setAssetsLoading(false);
        }
      }
    }
    loadAssets();
    return () => {
      isActive = false;
    };
  }, [form.business_unit_id]);

  const handleBusinessUnitChange = (businessUnitId) => {
    updateForm({ business_unit_id: businessUnitId, asset_ids: [], execution_profiles: {} });
  };

  const handleNeedCategoryChange = (needCategoryId) => {
    updateForm({ need_category_id: needCategoryId, matching_criteria: [] });
  };

  const handleImageUpload = async (file) => {
    if (!form.business_unit_id) {
      setError('Select a Business Unit before uploading an image.');
      return;
    }
    setImageUploading(true);
    setError('');
    try {
      const uploadResponse = await axiosInstance.post('/capability-media/upload', {
        partner_id: form.business_unit_id,
        filename: file.name,
        content_type: file.type || 'image/png',
        size_bytes: file.size,
        target_kind: 'capability',
      });
      const { url, fields, file_id: fileId } = uploadResponse?.data || {};
      if (!url || !fileId) {
        throw new Error('Image upload did not return a target URL.');
      }
      const uploadForm = new FormData();
      Object.entries(fields || {}).forEach(([key, value]) => {
        uploadForm.append(key, value);
      });
      uploadForm.append('file', file);
      const s3Response = await fetch(url, { method: 'POST', body: uploadForm });
      if (!s3Response.ok) {
        throw new Error('Image upload failed.');
      }
      updateForm({ capability_image_id: fileId });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to upload image.');
    } finally {
      setImageUploading(false);
    }
  };

  const validateBeforeSubmit = () => {
    if (!form.business_unit_id) {
      throw new Error('Business Unit is required.');
    }
    if (!form.name.trim()) {
      throw new Error('Capability Name is required.');
    }
    if (!form.description.trim()) {
      throw new Error('Description is required.');
    }
    if (!form.need_category_id) {
      throw new Error('Need Category is required.');
    }

    const latitudeText = String(form.latitude ?? '').trim();
    const longitudeText = String(form.longitude ?? '').trim();
    if (Boolean(latitudeText) !== Boolean(longitudeText)) {
      throw new Error('Latitude and Longitude must be provided together.');
    }
    if (latitudeText) {
      const latitude = Number(latitudeText);
      const longitude = Number(longitudeText);
      if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
        throw new Error('Latitude must be a number between -90 and 90.');
      }
      if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
        throw new Error('Longitude must be a number between -180 and 180.');
      }
    }

    const sharedKeys = new Set(sharedFields.map((field) => field.key));
    form.additional_inputs.forEach((input) => {
      const key = String(input.key || '').trim();
      if (key && sharedKeys.has(key)) {
        throw new Error(`Provider input "${key}" conflicts with a shared Need Category field.`);
      }
    });
  };

  const handlePreviewRequestForm = () => {
    try {
      additionalRequestSchemaFromInputs(form.additional_inputs);
      setError('');
      setPreviewOpen(true);
    } catch (err) {
      setError(err?.message || 'Unable to preview request form.');
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      validateBeforeSubmit();
      const payload = {
        business_unit_id: form.business_unit_id,
        name: form.name.trim(),
        description: form.description.trim(),
        capability_type: form.capability_type,
        capability_image_id: form.capability_image_id || null,
        location_address: form.location_address.trim() || null,
        latitude: coordinateForPayload(form.latitude),
        longitude: coordinateForPayload(form.longitude),
        need_category_id: form.need_category_id,
        source_capability_definition_id: slugifyCapabilityTitle(form.name),
        status: form.status,
        visibility: form.visibility,
        global_response: Boolean(form.list_on_capabilities_explorer),
        allow_direct_requests: Boolean(form.allow_direct_requests),
        acceptance_mode: form.acceptance_mode,
        fulfillment_mode: form.fulfillment_mode,
        matching_profile: matchingProfileFromCriteria(form.matching_criteria, sharedFields),
        additional_inputs: additionalInputsForPayload(form.additional_inputs),
        outputs: outputsForPayload(form.outputs),
        asset_ids: form.asset_ids,
        properties: {
          description: form.description.trim(),
          capability_type: form.capability_type,
          visibility: form.visibility,
          list_on_capabilities_explorer: Boolean(form.list_on_capabilities_explorer),
          allow_direct_requests: Boolean(form.allow_direct_requests),
          fulfillment_mode: form.fulfillment_mode,
        },
      };

      const response = await axiosInstance.post(
        `/partners/${encodeURIComponent(form.business_unit_id)}/capabilities`,
        payload,
      );
      const capabilityId = response.data?.capability_definition_id || response.data?.capability_id;
      const profileScopeId = response.data?.partner_id || response.data?.business_unit_id || form.business_unit_id;
      const profileEntries = Object.entries(form.execution_profiles || {}).filter(
        ([assetId, profile]) => form.asset_ids.includes(assetId) && profile?.selected_command_identifier,
      );
      let profileFailures = 0;
      if (capabilityId && profileEntries.length) {
        const profileResults = await Promise.allSettled(profileEntries.map(([assetId, profile]) => (
          axiosInstance.put(
            `/partners/${encodeURIComponent(profileScopeId)}/capabilities/${encodeURIComponent(capabilityId)}/assets/${encodeURIComponent(assetId)}/execution-profile`,
            profile,
          )
        )));
        profileFailures = profileResults.filter((result) => result.status === 'rejected').length;
      }
      navigate(capabilityId ? `/capabilities/${encodeURIComponent(capabilityId)}` : '/my-capabilities', {
        state: {
          feedback: profileFailures
            ? `${form.name.trim()} was created. ${profileFailures} execution profile${profileFailures === 1 ? '' : 's'} could not be saved and can be configured from the Assets tab.`
            : `${form.name.trim()} was created.`,
        },
      });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to create capability.');
    } finally {
      setSaving(false);
    }
  };

  const canContinue = [
    Boolean(form.business_unit_id && form.name.trim() && form.description.trim()),
    Boolean(form.need_category_id && !schemaLoading),
    true,
    true,
  ][activeStep];

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />

        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Capabilities', href: '/my-capabilities' },
              { label: 'New Capability' },
            ]}
          />

          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <PrecisionManufacturingRoundedIcon color="primary" />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    New Capability
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedBusinessUnit?.display_name || selectedBusinessUnit?.business_unit_code || 'Capability contract'}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button component={RouterLink} to="/my-capabilities" startIcon={<ArrowBackRoundedIcon />}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveRoundedIcon />}
                  onClick={handleSubmit}
                  disabled={saving || loading || !form.business_unit_id || !form.name.trim() || !form.description.trim() || !form.need_category_id}
                >
                  {saving ? 'Saving...' : 'Create'}
                </Button>
              </Stack>
            </Stack>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {loading ? (
                <Stack direction="row" spacing={2} alignItems="center">
                  <CircularProgress size={24} />
                  <Typography>Loading setup data...</Typography>
                </Stack>
              ) : (
                <Stack spacing={3}>
                  {activeStep === 0 ? (
                    <Stack spacing={2}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          select
                          label="Business Unit"
                          value={form.business_unit_id}
                          onChange={(event) => handleBusinessUnitChange(event.target.value)}
                          disabled={Boolean(scopedBusinessUnitId)}
                          required
                          fullWidth
                        >
                          {manageableBusinessUnits.map((unit) => (
                            <MenuItem key={unit.business_unit_id} value={unit.business_unit_id}>
                              {unit.display_name || unit.business_unit_code}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          label="Capability Type"
                          value={form.capability_type}
                          onChange={(event) => updateForm({ capability_type: event.target.value })}
                          required
                          fullWidth
                        >
                          {CAPABILITY_TYPES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                      <TextField
                        label="Capability Name"
                        value={form.name}
                        onChange={(event) => updateForm({ name: event.target.value })}
                        required
                        fullWidth
                      />
                      <TextField
                        label="Description"
                        value={form.description}
                        onChange={(event) => updateForm({ description: event.target.value })}
                        required
                        fullWidth
                        multiline
                        minRows={4}
                      />
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={Boolean(form.list_on_capabilities_explorer)}
                              onChange={(event) => updateForm({ list_on_capabilities_explorer: event.target.checked })}
                            />
                          )}
                          label="List on Explorer"
                        />
                        <Typography variant="body2" color="text.secondary">
                          Makes this capability discoverable in the Capabilities Explorer.
                        </Typography>
                      </Paper>
                      <CapabilityLocationFields
                        locationAddress={form.location_address}
                        latitude={form.latitude}
                        longitude={form.longitude}
                        onChange={updateForm}
                      />
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                          <InventoryImage
                            fileId={form.capability_image_id}
                            alt={form.name || 'Capability image'}
                            height={112}
                            sx={{ width: { xs: '100%', md: 168 }, flexShrink: 0 }}
                          />
                          <Stack spacing={1} sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              Image
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                              <Button component="label" variant="outlined" startIcon={<CloudUploadRoundedIcon />} disabled={imageUploading || !form.business_unit_id}>
                                {imageUploading ? 'Uploading...' : 'Upload Image'}
                                <input
                                  hidden
                                  type="file"
                                  accept="image/*"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    event.target.value = '';
                                    if (file) {
                                      handleImageUpload(file);
                                    }
                                  }}
                                />
                              </Button>
                              <Button color="error" disabled={!form.capability_image_id || imageUploading} onClick={() => updateForm({ capability_image_id: '' })}>
                                Remove
                              </Button>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Stack>
                  ) : null}

                  {activeStep === 1 ? (
                    <Stack spacing={2}>
                      <TextField
                        select
                        label="Need Category"
                        value={form.need_category_id}
                        onChange={(event) => handleNeedCategoryChange(event.target.value)}
                        required
                        fullWidth
                      >
                        {needCategories.map((category) => (
                          <MenuItem key={category.category_id} value={category.category_id}>
                            {category.category_display_name || category.category_name}
                          </MenuItem>
                        ))}
                      </TextField>
                      {schemaLoading ? (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <CircularProgress size={20} />
                          <Typography variant="body2">Loading shared fields...</Typography>
                        </Stack>
                      ) : (
                        <ContractFieldList
                          title={`Shared Need Information${selectedCategory ? `: ${selectedCategory.category_display_name || selectedCategory.category_name}` : ''}`}
                          fields={sharedFields}
                          emptyLabel="No shared fields are defined for this Need Category."
                          locked
                          collapsedByDefault
                        />
                      )}
                      <MatchingProfileEditor
                        criteria={form.matching_criteria}
                        sharedFields={sharedFields}
                        onChange={(matching_criteria) => updateForm({ matching_criteria })}
                      />
                    </Stack>
                  ) : null}

                  {activeStep === 2 ? (
                    <Stack spacing={2}>
                      <ContractFieldList
                        title="Shared Need Information"
                        fields={sharedFields}
                        emptyLabel="No inherited inputs."
                        locked
                        collapsedByDefault
                      />
                      <AdditionalInputsEditor
                        inputs={form.additional_inputs}
                        onChange={(additional_inputs) => updateForm({ additional_inputs })}
                        onPreview={handlePreviewRequestForm}
                      />
                      <OutputsEditor
                        outputs={form.outputs}
                        onChange={(outputs) => updateForm({ outputs })}
                      />
                    </Stack>
                  ) : null}

                  {activeStep === 3 ? (
                    <Stack spacing={2}>
                      <AssetPicker
                        assets={assets}
                        selectedAssetIds={form.asset_ids}
                        loading={assetsLoading}
                        onChange={(asset_ids) => updateForm({ asset_ids })}
                      />
                      <CapabilityExecutionProfiles
                        assets={selectedAssets}
                        capabilityInputs={executionProfileInputs}
                        drafts={form.execution_profiles}
                        onDraftsChange={(execution_profiles) => updateForm({ execution_profiles })}
                        fulfillmentMode={form.fulfillment_mode}
                      />
                      {form.business_unit_id ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button
                            component={RouterLink}
                            to={`/assets?business_unit_id=${encodeURIComponent(form.business_unit_id)}`}
                            variant="outlined"
                            startIcon={<Inventory2RoundedIcon />}
                          >
                            Open Assets
                          </Button>
                          <Button
                            component={RouterLink}
                            to={`/business-units/${encodeURIComponent(form.business_unit_id)}/assets/new?return_to=${encodeURIComponent(`${location.pathname}${location.search}`)}`}
                            variant="outlined"
                            startIcon={<AddCircleRoundedIcon />}
                          >
                            Create New Asset
                          </Button>
                        </Stack>
                      ) : null}
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={2}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <SettingsRoundedIcon color="primary" />
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              Publishing & Fulfillment
                            </Typography>
                          </Stack>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                              select
                              label="Status"
                              value={form.status}
                              onChange={(event) => updateForm({ status: event.target.value })}
                              fullWidth
                            >
                              {CAPABILITY_STATUSES.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              select
                              label="Visibility"
                              value={form.visibility}
                              onChange={(event) => updateForm({ visibility: event.target.value })}
                              fullWidth
                            >
                              {CAPABILITY_VISIBILITIES.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                              ))}
                            </TextField>
                          </Stack>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                              select
                              label="Fulfillment Mode"
                              value={form.fulfillment_mode}
                              onChange={(event) => updateForm({ fulfillment_mode: event.target.value })}
                              fullWidth
                            >
                              {FULFILLMENT_MODES.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              select
                              label="Acceptance Mode"
                              value={form.acceptance_mode}
                              onChange={(event) => updateForm({ acceptance_mode: event.target.value })}
                              fullWidth
                            >
                              {ACCEPTANCE_MODES.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                              ))}
                            </TextField>
                          </Stack>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={Boolean(form.allow_direct_requests)}
                                onChange={(event) => updateForm({ allow_direct_requests: event.target.checked })}
                              />
                            )}
                            label="Direct Requests"
                          />
                        </Stack>
                      </Paper>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip icon={<CheckCircleRoundedIcon />} label={optionLabel(CAPABILITY_TYPES, form.capability_type)} />
                          <Chip label={optionLabel(CAPABILITY_VISIBILITIES, form.visibility)} />
                          <Chip
                            label={form.list_on_capabilities_explorer ? 'Listed on Explorer' : 'Not listed on Explorer'}
                            color={form.list_on_capabilities_explorer ? 'success' : 'default'}
                            variant={form.list_on_capabilities_explorer ? 'filled' : 'outlined'}
                          />
                          <Chip label={optionLabel(FULFILLMENT_MODES, form.fulfillment_mode)} />
                          <Chip label={`${form.outputs.length} outputs`} />
                          <Chip label={`${form.asset_ids.length} assets`} />
                        </Stack>
                      </Paper>
                    </Stack>
                  ) : null}

                  <Divider />
                  <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Button
                      startIcon={<ArrowBackRoundedIcon />}
                      disabled={activeStep === 0}
                      onClick={() => setActiveStep((current) => Math.max(0, current - 1))}
                    >
                      Back
                    </Button>
                    {activeStep < steps.length - 1 ? (
                      <Button
                        variant="contained"
                        endIcon={<ArrowForwardRoundedIcon />}
                        disabled={!canContinue}
                        onClick={() => setActiveStep((current) => Math.min(steps.length - 1, current + 1))}
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        startIcon={<SaveRoundedIcon />}
                        onClick={handleSubmit}
                        disabled={saving || !form.business_unit_id || !form.name.trim() || !form.description.trim() || !form.need_category_id}
                      >
                        {saving ? 'Saving...' : 'Create Capability'}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              )}
            </Paper>
            <RequestFormPreviewDialog
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              sharedFields={sharedFields}
              providerFields={capabilityAdditionalFields}
            />
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
